// Skeletal animation data — no drawing code, and no trig. The figure's geometry
// (content/figure.js) turns this into joint angles; the renderer just draws them.
//
// The VALUES live in data/animations.json, which is the source of truth the tools
// (meta/tasks.yaml) read and write. This module is the documentation and the
// contract around them — JSON can carry neither.
//
// Angles are ABSOLUTE, measured from straight-down, positive = toward the figure's
// facing direction (+x before the sprite is flipped). Every limb has two segments:
// a leg is `thigh` (hip→knee) + `shin` (knee→foot); an arm is `upper` (shoulder→elbow)
// + `fore` (elbow→hand).
//
// THE KNEE RULE: a knee only bends backwards — the knee leads, the heel trails. Because
// angles are absolute and +ve is forwards, that means `shin <= thigh` in EVERY pose.
//
// THE ELBOW RULE: an elbow bends the other way — the forearm leads. So `fore >= upper`,
// which for the free arm means its `armBend` is never negative.
//
// Either one broken is a limb snapping the wrong way, which a stick figure's silhouette
// shows off worse than anything else. figure.js's IK obeys both by construction (they're
// the same solve with one sign flipped); anything hand-authored here has to be checked
// against them, and validate() does — across every gait AND the blends between them.
//
// The AIMING arm is exempt: a shoulder swings through a full circle (you can aim behind
// you), so it has no fixed bend direction relative to the body. It's solved to hang its
// elbow below the shoulder→hand line instead — see aimArm() in figure.js.

import ANIMATIONS from "../../data/animations.json" with { type: "json" };

// The gaits. Each is `{ duty, stride, armSwing, swing[] }`:
//
//   duty     Fraction of the cycle each foot spends PLANTED. Above 0.5 the two
//            stances overlap (both feet down for a moment) — that double-support
//            phase is exactly what makes a walk a walk. Below 0.5 the figure is
//            airborne between steps: that flight time is what makes a run a run,
//            and it's what buys the run its much longer stride.
//
//   stride   Ground covered per cycle, in body radii. THE key number: cadence is
//            not a free parameter, it's speed / stride. figure.js clamps it to what
//            the legs can reach at the figure's ride height — to step further you
//            must ride lower (see RIDE in data/figure.json).
//
//   armSwing Peak swing of the free arm's SHOULDER, radians; it counter-swings against
//            the leading leg. Cheap, and most of what sells the motion.
//
//   armBend  How far the free arm's elbow is folded, radians. Blended like everything
//            else, so a walk's arm hangs nearly straight and a run's is folded toward
//            90° — that fold is a lot of what separates the two at a glance. Must be
//            >= 0 (see THE ELBOW RULE below).
//
//   swing[]  The SWING leg only: the foot's path through the air, u = 0 at toe-off,
//            u = 1 at heel strike. The planted leg is NOT keyframed — it's solved by
//            IK so the foot stays welded to the ground while the hips travel over it
//            (a hand-authored stance foot slides, which is what "not really walking"
//            looks like). u = 0 and u = 1 come from that same IK, so the swing leaves
//            and lands exactly on the stance pose with no pop; author only what's in
//            between. Walk and run must share their `u` keys so the two can blend.
export const GAITS = ANIMATIONS.gaits;

// Walk → run, by speed. Measured in BODY RADII per second, not
// pixels: a big figure moving at 300px/s is strolling where a small
// one is sprinting, so the gait has to follow the figure, not the
// screen.
//
// Why blend at all: the player covers ~7 body-heights/second — it is
// sprinting, and no walk cycle looks right at that speed (its legs
// would cycle 13x/second, a blur). The slow grunts, meanwhile,
// genuinely walk. One cycle cannot do both, because cadence is
// speed/stride and stride is capped by leg reach. So: two cycles,
// blended.
export const BLEND = ANIMATIONS.blend;

// Idle: the figure just stands at its ride height, legs solved by
// figure.js. Both legs are posed IDENTICALLY, which matters — swing
// angles are measured about the FACING direction, so a left/right
// difference is really a forward/back difference, and mirroring the
// sprite to face the other way visibly swaps an asymmetric stance's
// legs over. That's why standing used to look different facing left
// vs right. Identical legs, separated only by the hips' x offset,
// look the same either way. `armAngle` just hangs the free arm a
// little behind the body.
export const STAND = ANIMATIONS.stand;
